import { Head, Link, router, usePage } from '@inertiajs/react'
import { ArrowLeft, Check } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/Dialog'
import { toast } from 'sonner'
import MainLayout from '@/components/Layout/MainLayout'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { Switch } from '@/components/ui/Switch'
import { TagInput } from '@/components/ui/TagInput'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/Select'
import { ArticleContentEditor, type ArticleContent } from '@/components/help_center/ArticleContentEditor'
import { ArticleCoverCard } from '@/components/help_center/ArticleCoverCard'
import { useRouteParams } from '@/hooks/useRoutes'
import routes from '@/routeHelpers'
import type { CurrentUser } from '@/types/shared'
import type { HelpArticleFull, HelpCategoryNode } from './types'

interface ArticleEditorProps {
  current_user: CurrentUser
  article: HelpArticleFull | null
  categories: HelpCategoryNode[]
  linkable_articles: { slug: string; title: string; published: boolean }[]
  /** Tags already in use, for the tag field's type-ahead: a near-miss
   *  spelling is a second tag nothing else shares. */
  available_tags: string[]
  brand_primary: string | null
  brand_accent: string | null
}

const NO_CATEGORY = '__none__'

export default function ArticleEditor({ current_user, article, categories, linkable_articles, available_tags, brand_primary, brand_accent }: ArticleEditorProps) {
  const { withAccount } = useRouteParams()
  const { flash } = usePage<{ flash: { notice?: string; alert?: string } }>().props
  const editing = Boolean(article)

  const [title, setTitle] = useState(article?.title ?? '')
  const [summary, setSummary] = useState(article?.summary ?? '')
  const [categoryId, setCategoryId] = useState(article?.help_category_id ?? NO_CATEGORY)
  const [published, setPublished] = useState(article?.published ?? false)
  const [featured, setFeatured] = useState(article?.featured ?? false)
  const [seoTitle, setSeoTitle] = useState(article?.seo_title ?? '')
  const [seoDescription, setSeoDescription] = useState(article?.seo_description ?? '')
  const [tags, setTags] = useState<string[]>(article?.tags ?? [])
  const [content, setContent] = useState<ArticleContent>({
    json: article?.content_json ?? {},
    html: article?.rendered_html ?? '',
  })

  // Dirty tracking compares the live state against a saved baseline. An Inertia
  // save re-renders this page WITHOUT remounting, so we reset the baselines on
  // save success rather than relying on fresh props. Content uses the editor's
  // normalized JSON (via onReady) so add-a-space/remove-a-space reads clean.
  const fieldsSnapshot = () =>
    JSON.stringify({ title, summary, categoryId, published, featured, seoTitle, seoDescription, tags })
  const [savedFields, setSavedFields] = useState(fieldsSnapshot)
  const [contentJson, setContentJson] = useState<string | null>(null)
  const [savedContentJson, setSavedContentJson] = useState<string | null>(null)

  const dirty =
    fieldsSnapshot() !== savedFields ||
    (contentJson !== null && contentJson !== savedContentJson)

  const handleContentReady = (json: Record<string, unknown>) => {
    const str = JSON.stringify(json)
    setContentJson(str)
    setSavedContentJson(str)
  }
  const handleContentChange = (value: ArticleContent) => {
    setContent(value)
    setContentJson(JSON.stringify(value.json))
  }

  useEffect(() => {
    if (flash?.notice) toast.success(flash.notice)
    if (flash?.alert) toast.error(flash.alert)
  }, [flash?.notice, flash?.alert])

  // Unsaved-changes guard: cancel GET navigations while dirty and ask via our
  // dialog (native confirm is banned); saves (non-GET) pass through, and the
  // post-save redirect is whitelisted with bypassRef. beforeunload covers tab
  // close/refresh with the browser's built-in prompt.
  const dirtyRef = useRef(dirty)
  dirtyRef.current = dirty
  const bypassRef = useRef(false)
  const [leaveUrl, setLeaveUrl] = useState<string | null>(null)

  useEffect(() => {
    const offBefore = router.on('before', event => {
      const { visit } = event.detail
      if (bypassRef.current || !dirtyRef.current || visit.method !== 'get') return
      setLeaveUrl(visit.url.href)
      return false
    })
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (dirtyRef.current) event.preventDefault()
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => {
      offBefore()
      window.removeEventListener('beforeunload', onBeforeUnload)
    }
  }, [])

  const confirmLeave = () => {
    if (!leaveUrl) return
    bypassRef.current = true
    router.visit(leaveUrl)
  }

  const save = () => {
    const payload = {
      help_article: {
        title,
        summary,
        help_category_id: categoryId === NO_CATEGORY ? null : categoryId,
        content_json: JSON.stringify(content.json),
        rendered_html: content.html,
        published,
        featured,
        seo_title: seoTitle,
        seo_description: seoDescription,
        tags,
      },
    }
    // The save's redirect is a GET visit — don't let the guard eat it.
    bypassRef.current = true
    const opts = {
      preserveScroll: true,
      onSuccess: () => {
        // No remount on save, so advance the baselines → button reads "Saved".
        setSavedFields(fieldsSnapshot())
        if (contentJson !== null) setSavedContentJson(contentJson)
        bypassRef.current = false
      },
      onError: () => { bypassRef.current = false },
    }
    if (editing && article) {
      router.patch(routes.helpArticles.update.path(withAccount({ id: article.slug })), payload, opts)
    } else {
      router.post(routes.helpArticles.create.path(withAccount()), payload, opts)
    }
  }

  return (
    <>
      <Head title={editing ? title || 'Edit article' : 'New article'} />
      <MainLayout user={current_user} breadcrumbs={[{ label: 'Help center', href: routes.helpArticles.index.path(withAccount()) }, { label: editing ? 'Edit article' : 'New article' }]}>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="mb-4 flex items-center justify-between gap-3">
            <Button variant="ghost" size="sm" asChild>
              <Link href={routes.helpArticles.index.path(withAccount())}><ArrowLeft className="size-4" /> Back</Link>
            </Button>
            <Button onClick={save} disabled={!dirty || !title.trim()}>
              <Check className="size-4" /> {dirty ? 'Save' : 'Saved'}
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_20rem]">
            <div className="space-y-4 min-w-0">
              <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Article title" className="text-lg font-semibold" />
              <Input value={summary} onChange={e => setSummary(e.target.value)} placeholder="Short summary (shown in listings)" />
              <ArticleContentEditor
                initialContent={article?.content_json ?? null}
                fallbackHtml={article?.rendered_html}
                articles={linkable_articles}
                accountSlug={withAccount().account_id ?? ''}
                onReady={handleContentReady}
                onChange={handleContentChange}
              />
            </div>

            <div className="space-y-4">
              <Card>
                <CardHeader><CardTitle className="text-base">Article settings</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select value={categoryId} onValueChange={setCategoryId}>
                      <SelectTrigger><SelectValue placeholder="Uncategorized" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NO_CATEGORY}>Uncategorized</SelectItem>
                        {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="article-tags">Tags</Label>
                    <TagInput id="article-tags" value={tags} onChange={setTags} suggestions={available_tags} placeholder="billing, refunds…" />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Featured</Label>
                    <Switch checked={featured} onCheckedChange={setFeatured} />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>{published ? 'Published' : 'Draft'}</Label>
                    <Switch checked={published} onCheckedChange={setPublished} />
                  </div>
                </CardContent>
              </Card>

              {editing && article && (
                <ArticleCoverCard
                  article={article}
                  categories={categories}
                  categoryId={categoryId}
                  brandPrimary={brand_primary}
                  brandAccent={brand_accent}
                />
              )}

              <Card>
                <CardHeader><CardTitle className="text-base">Search engine listing</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>SEO title <span className="text-xs text-muted-foreground">{seoTitle.length}/60</span></Label>
                    <Input value={seoTitle} onChange={e => setSeoTitle(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Meta description <span className="text-xs text-muted-foreground">{seoDescription.length}/160</span></Label>
                    <Input value={seoDescription} onChange={e => setSeoDescription(e.target.value)} />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </MainLayout>

      <Dialog open={leaveUrl !== null} onOpenChange={open => { if (!open) setLeaveUrl(null) }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="text-left">
            <DialogTitle>Discard unsaved changes?</DialogTitle>
            <DialogDescription>This article has changes that haven't been saved.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setLeaveUrl(null)}>Keep editing</Button>
            <Button variant="destructive" onClick={confirmLeave}>Discard changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
